/**
 * Utility functions for agent/CCP status handling
 * Used across manager components to ensure consistent status display
 */

/**
 * Maps agent CCP status to Material-UI Chip color
 * @param status - The agent's current CCP status (e.g., 'Available', 'Offline', 'Break', etc.)
 * @returns MUI Chip color value
 */
export const getStatusChipColor = (
  status: string
): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  const normalizedStatus = status?.toLowerCase() || '';
  if (normalizedStatus.includes('available')) return 'success';
  if (normalizedStatus.includes('offline')) return 'error';
  if (normalizedStatus.includes('break') || normalizedStatus.includes('lunch'))
    return 'warning';
  if (
    normalizedStatus.includes('after call work') ||
    normalizedStatus.includes('acw')
  )
    return 'info';
  return 'default';
};

/**
 * Maps agent CCP status to Pill color component
 * @param status - The agent's current CCP status
 * @returns Pill color value ('success' | 'error' | 'default')
 */
export const getStatusPillColor = (
  status?: string
): 'success' | 'error' | 'default' => {
  switch (status) {
    case 'Available':
      return 'success';
    case 'Offline':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Maps agent CCP status to Tailwind CSS background color
 * @param status - The agent's current CCP status
 * @returns Tailwind CSS class
 */
export const getCCPStatusColor = (status?: string): string => {
  switch (status) {
    case 'Available':
      return 'bg-green-500';
    case 'Offline':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
};

/**
 * Gets the translated label for agent status
 * Must be called within a component that has i18next useTranslation hook
 * @param status - The agent's current CCP status
 * @param t - i18next translation function
 * @returns Translated status label
 */
export const getStatusLabel = (
  status: string,
  t: (key: string) => string
): string => {
  const normalizedStatus = status?.toLowerCase() || '';
  if (normalizedStatus.includes('available'))
    return t('components.manager.liveAgentStatus.available');
  if (
    normalizedStatus.includes('on contact') ||
    normalizedStatus.includes('on-call') ||
    normalizedStatus.includes('oncall') ||
    normalizedStatus.includes('calling') ||
    (normalizedStatus.includes('busy') && !normalizedStatus.includes('pending'))
  )
    return t('components.manager.liveAgentStatus.onCall');
  if (normalizedStatus.includes('offline'))
    return t('components.manager.liveAgentStatus.offline');
  if (normalizedStatus.includes('break'))
    return t('components.manager.liveAgentStatus.onBreak');
  if (
    normalizedStatus.includes('after call work') ||
    normalizedStatus.includes('acw')
  )
    return t('components.manager.liveAgentStatus.afterCallWork');
  return status || t('components.manager.liveAgentStatus.unknown');
};
